import {useDisclosure} from '@mantine/hooks';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useGetGroupedSupportCompanies} from '../../../../../core/repositories/companyRepository';
import type {
  ImageAnalyzeStepResponse,
  StepCandidateType,
  TextAnalyzeStepResponse,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../../shared/components';
import type {StepCreationTriggerType} from '../../../constants/entrySourceTypes';
import {useCompanyList} from '../../../usecase/useCompanyList';
import {StepCandidatePreviewModal} from '../Presenter/Progress/StepCandidatePreviewModal';

export type StepCandidateValidationErrors = {
  companyName?: string;
  candidates?: Record<number, {stepName?: string}>;
};

export type StepCandidatePreviewModalRef = {
  open: (
    result: TextAnalyzeStepResponse | ImageAnalyzeStepResponse,
    entrySourceType: StepCreationTriggerType,
  ) => void;
  close: () => void;
};

type Props = {
  onSave: (
    companyName: string | null,
    companyID: number | null,
    userEntryManagementID: number | undefined,
    candidate: StepCandidateType,
    entrySourceType: StepCreationTriggerType,
    showNotification?: boolean,
  ) => Promise<{stepID?: number; userEntryManagementID?: number} | undefined>;
};

export const StepCandidatePreviewModalContainer = forwardRef<
  StepCandidatePreviewModalRef,
  Props
>(({onSave}, ref) => {
  const [opened, {open, close}] = useDisclosure(false);
  const [analyzeResult, setAnalyzeResult] = useState<
    (TextAnalyzeStepResponse | ImageAnalyzeStepResponse) | null
  >(null);
  const [sourceType, setSourceType] = useState<StepCreationTriggerType | null>(
    null,
  );
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [savedSteps, setSavedSteps] = useState<number[]>([]);
  const [isSavingAll, setIsSavingAll] = useState(false);

  // 編集状態の管理
  const [editedCompanyName, setEditedCompanyName] = useState<string>('');
  const [editedCompanyId, setEditedCompanyId] = useState<number>(0);
  const [editedCandidates, setEditedCandidates] = useState<StepCandidateType[]>(
    [],
  );
  const [hasResolvedCompanyId, setHasResolvedCompanyId] = useState(false);

  // APIレスポンスの初期値を保持（フィールド表示判定用）
  const initialCandidatesRef = useRef<StepCandidateType[]>([]);

  // 企業データの取得
  const {data: companiesData} = useGetGroupedSupportCompanies({
    queryParams: {},
    config: {
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: 30 * 60 * 1000,
    },
  });
  const companyList = useCompanyList(companiesData, {format: 'autocomplete'});

  // 企業名から企業IDを解決（モーダルオープン時やcompanyListロード後）
  // biome-ignore lint/correctness/useExhaustiveDependencies: openedとcompanyListの変更時のみ実行したいため
  useEffect(() => {
    if (
      opened &&
      editedCompanyName &&
      companyList.length > 0 &&
      !hasResolvedCompanyId
    ) {
      const matchedCompany = companyList.find(
        (company) => company.value === editedCompanyName,
      );
      setEditedCompanyId(matchedCompany?.id ?? 0);
      setHasResolvedCompanyId(true);
    }
  }, [opened, companyList]);

  // バリデーションエラーの計算
  const validationErrors = useMemo((): StepCandidateValidationErrors => {
    const errors: StepCandidateValidationErrors = {};

    // 企業名の空チェック
    if (!editedCompanyName.trim()) {
      errors.companyName = '会社名を入力してください';
    }

    // ステップ名の空チェック
    const candidateErrors: Record<number, {stepName?: string}> = {};
    editedCandidates.forEach((candidate, index) => {
      if (!candidate.stepName?.trim()) {
        candidateErrors[index] = {stepName: '選考予定を入力してください'};
      }
    });
    if (Object.keys(candidateErrors).length > 0) {
      errors.candidates = candidateErrors;
    }

    return errors;
  }, [editedCompanyName, editedCandidates]);

  // 企業名変更ハンドラ
  const handleCompanyNameChange = useCallback((value: string) => {
    setEditedCompanyName(value);
    setHasResolvedCompanyId(false); // 企業名が変更されたら再解決が必要
  }, []);

  // 企業名blur時のハンドラ（企業IDを解決）
  const handleCompanyNameBlur = useCallback(() => {
    const matchedCompany = companyList.find(
      (company) => company.value === editedCompanyName,
    );
    setEditedCompanyId(matchedCompany?.id ?? 0);
    setHasResolvedCompanyId(true);
  }, [companyList, editedCompanyName]);

  // 企業選択時のハンドラ（ドロップダウンから選択した場合、即座にIDを解決）
  const handleCompanySelect = useCallback(
    (companyId: number, companyName: string) => {
      setEditedCompanyName(companyName);
      setEditedCompanyId(companyId);
      setHasResolvedCompanyId(true);
    },
    [],
  );

  // 候補フィールド変更ハンドラ
  const handleCandidateFieldChange = useCallback(
    (
      index: number,
      field: keyof StepCandidateType,
      value: string | undefined,
    ) => {
      setEditedCandidates((prev) =>
        prev.map((candidate, i) =>
          i === index ? {...candidate, [field]: value} : candidate,
        ),
      );
    },
    [],
  );

  /**
   * 企業名からcompanyIDを解決する
   * companyListから完全一致する企業を検索し、見つかればそのIDを返す
   */
  const resolveCompanyID = useCallback(
    (
      companyID: number | null | undefined,
      companyName: string | null,
    ): number | null => {
      // 既にcompanyIDが設定されている場合はそのまま返す
      if (companyID && companyID > 0) {
        return companyID;
      }

      // companyNameがない場合はnullを返す
      if (!companyName) {
        return null;
      }

      // companyListから企業名が完全一致するものを検索
      const matchedCompany = companyList.find(
        (company) => company.value === companyName,
      );

      return matchedCompany?.id ?? null;
    },
    [companyList],
  );

  // 状態リセット用の共通関数
  const resetState = useCallback(() => {
    setAnalyzeResult(null);
    setSourceType(null);
    setSavingIndex(null);
    setSavedSteps([]);
    setIsSavingAll(false);
    setEditedCompanyName('');
    setEditedCompanyId(0);
    setEditedCandidates([]);
    setHasResolvedCompanyId(false);
    initialCandidatesRef.current = [];
  }, []);

  useImperativeHandle(ref, () => ({
    open: (
      result: TextAnalyzeStepResponse | ImageAnalyzeStepResponse,
      sourceType: StepCreationTriggerType,
    ) => {
      setAnalyzeResult(result);
      setSourceType(sourceType);
      setSavedSteps([]);
      // 編集状態を初期化
      setEditedCompanyName(result.companyName ?? '');
      setEditedCompanyId(result.companyID ?? 0);
      setEditedCandidates(result.candidates ?? []);
      // 初期値を保持（フィールド表示判定用）
      initialCandidatesRef.current = result.candidates ?? [];
      // APIからcompanyIDが提供されている場合は解決済みとする
      setHasResolvedCompanyId(!!result.companyID && result.companyID > 0);
      open();
    },
    close: () => {
      close();
      resetState();
    },
  }));

  const handleSave = async (
    _selectedCandidate: StepCandidateType,
    index: number,
  ) => {
    if (!analyzeResult || !sourceType) return;

    // 編集後の企業IDを使用（0の場合は企業名から解決を試みる）
    const resolvedCompanyID =
      editedCompanyId > 0
        ? editedCompanyId
        : resolveCompanyID(null, editedCompanyName);

    // 編集後のステップ情報を使用
    const editedCandidate = editedCandidates[index];
    if (!editedCandidate) {
      showErrorSnackBar({
        title: 'エラー',
        message:
          '候補データの整合性に問題が発生しました。もう一度お試しください。',
      });
      return;
    }

    setSavingIndex(index);
    try {
      const result = await onSave(
        editedCompanyName || null,
        resolvedCompanyID,
        analyzeResult.userEntryManagementID,
        editedCandidate,
        sourceType,
      );

      // 保存成功時にsavedStepsに追加
      if (result?.stepID) {
        setSavedSteps((prev) => [...prev, index]);
      }
    } finally {
      setSavingIndex(null);
    }
  };

  const isStepSaved = (index: number): boolean => {
    return savedSteps.includes(index);
  };

  /**
   * 未保存の選考予定を一括保存
   */
  const handleSaveAll = async () => {
    if (!analyzeResult || !sourceType) {
      showErrorSnackBar({
        title: 'エラー',
        message: '保存に必要な情報が不足しています',
      });
      return;
    }

    // 編集後の企業IDを使用（0の場合は企業名から解決を試みる）
    const resolvedCompanyID =
      editedCompanyId > 0
        ? editedCompanyId
        : resolveCompanyID(null, editedCompanyName);

    setIsSavingAll(true);

    // 編集後の候補を使用
    const candidates = editedCandidates;
    const unsavedIndices = candidates
      .map((_, index) => index)
      .filter((index) => !isStepSaved(index));

    if (unsavedIndices.length === 0) {
      showErrorSnackBar({
        title: 'エラー',
        message: '保存する選考予定がありません',
      });
      setIsSavingAll(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    // 未保存の選考予定を順次保存
    for (const index of unsavedIndices) {
      setSavingIndex(index);

      const candidate = candidates[index];
      if (!candidate) {
        failCount++;
        continue;
      }

      try {
        const result = await onSave(
          editedCompanyName || null,
          resolvedCompanyID,
          analyzeResult.userEntryManagementID,
          candidate,
          sourceType,
          false, // 一括保存時は個別の通知を抑制
        );

        if (result?.stepID) {
          // 保存成功
          setSavedSteps((prev) => [...prev, index]);
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error('[Step Save All] Error saving step:', error);
        failCount++;
      }
    }

    setSavingIndex(null);
    setIsSavingAll(false);

    // 結果通知
    if (failCount === 0) {
      showSuccessSnackBar({
        title: '選考予定保存が完了しました',
        message: `${successCount}件の選考予定を保存しました`,
      });
    } else {
      showErrorSnackBar({
        title: '選考予定保存の一部が失敗しました',
        message: `${successCount}件保存、${failCount}件失敗しました`,
      });
    }
  };

  const handleClose = () => {
    close();
    resetState();
  };

  return (
    <StepCandidatePreviewModal
      opened={opened}
      onClose={handleClose}
      onSave={handleSave}
      onSaveAll={handleSaveAll}
      companyName={editedCompanyName}
      companyList={companyList}
      onCompanyNameChange={handleCompanyNameChange}
      onCompanyNameBlur={handleCompanyNameBlur}
      onCompanySelect={handleCompanySelect}
      candidates={editedCandidates}
      initialCandidates={initialCandidatesRef.current}
      onCandidateFieldChange={handleCandidateFieldChange}
      savingIndex={savingIndex}
      isSavingAll={isSavingAll}
      isStepSaved={isStepSaved}
      validationErrors={validationErrors}
    />
  );
});

StepCandidatePreviewModalContainer.displayName =
  'StepCandidatePreviewModalContainer';
